import { useState, useEffect, useContext } from 'react';

import type { Provider, CollabEditProvider } from '@atlaskit/collab-provider';

import {
	EDIT_PAGE_LOAD_EXPERIENCE,
	COMPANY_HUB_EDITOR_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { getLivePageViewExperienceName } from '@confluence/experience-tracker/entry-points/livePageExperiences';
import { getLogger } from '@confluence/logger';
import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';

import { useNativeCollabState } from './useNativeCollabState';

const logger = getLogger('native-collab');

export const useInit = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	contentId: string,
	isCompanyHub?: boolean,
) => {
	const [{ collabEditInitialized }, nativeCollabActions] = useNativeCollabState();

	const [title, setTitle] = useState<string | null>(null);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const isLivePage = useIsCurrentPageLive();

	let currentEditorPageLoadExperienceName = EDIT_PAGE_LOAD_EXPERIENCE;
	if (isLivePage) {
		currentEditorPageLoadExperienceName = getLivePageViewExperienceName();
	}
	if (isCompanyHub) {
		currentEditorPageLoadExperienceName = COMPANY_HUB_EDITOR_LOAD_EXPERIENCE;
	}
	const { livePageReleasePhase } = useIsLivePagesFeatureEnabled();

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleInit = (data) => {
			logger.debug`Collab Provider initialised at ${performance.now() / 1000}`;
			const newTitle = data?.metadata?.title ?? '';
			setTitle(newTitle);
			nativeCollabActions.setCollabEditInitialized(true);
			// marking provider initialization as complete once FE receives "init" event
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_DISABLED,
			);
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_INIT_DOC,
			);
			experienceTracker.succeed({
				name: currentEditorPageLoadExperienceName,
				attributes: {
					collabService: 'ncs',
					isLivePage,
					...(isLivePage ? { livePageReleasePhase } : {}),
				},
			});
		};
		collabEditProvider.on('init', handleInit);
		return () => {
			collabEditProvider.off('init', handleInit);

			const experienceState = experienceTracker.getExperienceState(
				currentEditorPageLoadExperienceName,
			);
			const experienceHasStopped = experienceState?.hasStopped ?? true;
			const experienceMatchesContentId = experienceState?.attributes?.contentId === contentId;

			// If we leave the editor page and the page we transition to immediately calls `useInit` (e.g. going from edit/live page to live page), this logic may undesirably try to abort the newly started experience. Using `experienceMatchesContentId` prevents this.
			if (!experienceHasStopped && experienceMatchesContentId) {
				experienceTracker.abort({
					name: currentEditorPageLoadExperienceName,
					reason: 'Unmounted before experience finished',
					attributes: {
						isLivePage,
					},
				});
			}
		};
	}, [
		collabEditProvider,
		experienceTracker,
		nativeCollabActions,
		currentEditorPageLoadExperienceName,
		isLivePage,
		livePageReleasePhase,
		contentId,
	]);

	return {
		collabEditInitialized,
		titleFromInit: title,
	};
};
